import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

interface FeaturesListProps {
  features: string[];
  onChange: (features: string[]) => void;
  errors?: string[];
}

const FeaturesList: React.FC<FeaturesListProps> = ({ features, onChange, errors }) => {
  const handleAddFeature = () => {
    if (features.length < 20) {
      onChange([...features, '']);
    }
  };

  const handleRemoveFeature = (index: number) => {
    onChange(features.filter((_, i) => i !== index));
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    onChange(newFeatures);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Features
        <span className="text-gray-500 ml-2">({features.length}/20)</span>
      </label>

      <div className="space-y-2 mb-3">
        {features.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No features added yet</p>
        ) : (
          features.map((feature, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={feature}
                onChange={e => handleFeatureChange(index, e.target.value)}
                placeholder={`Feature ${index + 1}`}
                maxLength={100}
                className="flex-1 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-[#23F47C] transition-colors placeholder-gray-500"
              />
              <button
                type="button"
                onClick={() => handleRemoveFeature(index)}
                className="px-3 py-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                title="Remove feature"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add Feature Button */}
      {features.length < 20 && (
        <button
          type="button"
          onClick={handleAddFeature}
          className="inline-flex items-center gap-2 text-sm text-[#23F47C] hover:text-[#1dd45f] transition-colors"
        >
          <Plus size={16} />
          Add Feature
        </button>
      )}

      {/* Validation Errors */}
      {errors && errors.length > 0 && (
        <div className="mt-2 space-y-1">
          {errors.map((error, index) => (
            <p key={index} className="text-sm text-red-400">
              {error}
            </p>
          ))}
        </div>
      )}

      {/* Max Features Warning */}
      {features.length >= 20 && (
        <p className="mt-2 text-sm text-yellow-400">Maximum 20 features reached</p>
      )}
    </div>
  );
};

export default FeaturesList;
